import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import DataTable, { type DataColumn } from "@/components/base/DataTable";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import AdminPageHeader from "../components/AdminPageHeader";
import AdminToolbar from "../components/AdminToolbar";
import { useAuth } from "@/context/AuthContext";
import {
  createAdminLocation,
  deleteAdminLocation,
  getAdminDistricts,
  getAdminLocations,
  updateAdminLocation,
} from "@/services/adminApi";
import type { AdminLocation, DistrictOption } from "@/types";

const slugify = (value: string) =>
  value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function AdminLocations() {
  const { token } = useAuth();
  const [rows, setRows] = useState<AdminLocation[]>([]);
  const [districts, setDistricts] = useState<DistrictOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [districtId, setDistrictId] = useState("");
  const [description, setDescription] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<AdminLocation | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    Promise.all([getAdminLocations(token), getAdminDistricts(token)])
      .then(([locs, dists]) => {
        if (!active) return;
        setRows(locs);
        setDistricts(dists);
        if (dists[0]) setDistrictId(String(dists[0].id));
      })
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2200);
  };

  const flashError = (msg: string) => {
    setActionError(msg);
    setTimeout(() => setActionError(""), 3200);
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return rows.filter((r) => !q || r.name.toLowerCase().includes(q) || r.districtName.toLowerCase().includes(q));
  }, [rows, query]);

  const resetForm = () => {
    setName("");
    setDescription("");
    setEditingId(null);
    setShowForm(false);
    if (districts[0]) setDistrictId(String(districts[0].id));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || !districtId) return;
    setSaving(true);
    const districtName = districts.find((d) => String(d.id) === districtId)?.name ?? "";

    if (editingId) {
      const ok = await updateAdminLocation(
        editingId,
        { name: trimmed, slug: slugify(trimmed), districtId, description: description.trim() },
        token,
      );
      if (!ok) {
        setSaving(false);
        flashError("Couldn't save that location. Please try again.");
        return;
      }
      setRows((prev) =>
        prev.map((r) =>
          r.id === editingId
            ? { ...r, name: trimmed, slug: slugify(trimmed), districtId, districtName, description: description.trim() }
            : r,
        ),
      );
      flash(`Location “${trimmed}” updated.`);
    } else {
      const created = await createAdminLocation(
        { name: trimmed, slug: slugify(trimmed), districtId, description: description.trim() },
        token,
      );
      if (!created) {
        setSaving(false);
        flashError("Couldn't create that location. Please try again.");
        return;
      }
      setRows((prev) => [...prev, { ...created, districtName: created.districtName || districtName }]);
      flash(`Location “${trimmed}” created.`);
    }

    setSaving(false);
    resetForm();
  };

  const startEdit = (row: AdminLocation) => {
    setEditingId(row.id);
    setName(row.name);
    setDescription(row.description);
    setDistrictId(String(row.districtId));
    setShowForm(true);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (row: AdminLocation) => {
    const snapshot = rows;
    setRows((prev) => prev.filter((r) => r.id !== row.id));
    const ok = await deleteAdminLocation(row.id, token);
    if (!ok) {
      setRows(snapshot);
      flashError("Couldn't remove that location. Please try again.");
      return;
    }
    if (editingId === row.id) resetForm();
    flash("Location removed.");
  };

  const columns: DataColumn<AdminLocation>[] = [
    {
      key: "name",
      header: "Location",
      render: (r) => (
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
            <i className="ri-map-pin-line"></i>
          </span>
          <div>
            <p className="font-semibold text-foreground-950">{r.name}</p>
            <p className="text-xs text-foreground-500">/{r.slug}</p>
          </div>
        </div>
      ),
    },
    { key: "districtName", header: "District", render: (r) => <span className="text-foreground-700">{r.districtName}</span>, nowrap: true },
    { key: "description", header: "Description", render: (r) => <span className="text-foreground-500">{r.description}</span> },
    { key: "businessCount", header: "Listings", align: "right", render: (r) => <span className="font-semibold text-foreground-900">{r.businessCount.toLocaleString()}</span>, nowrap: true },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} />, nowrap: true },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      nowrap: true,
      render: (r) => (
        <div className="flex items-center justify-end gap-1.5">
          <Link
            to={`/location/${r.slug}`}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 cursor-pointer"
            aria-label="View location"
          >
            <i className="ri-external-link-line"></i>
          </Link>
          <button
            type="button"
            onClick={() => startEdit(r)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 cursor-pointer"
            aria-label="Edit location"
          >
            <i className="ri-edit-line"></i>
          </button>
          <button
            type="button"
            onClick={() => setPendingDelete(r)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-secondary-300 text-secondary-700 hover:bg-secondary-50 cursor-pointer"
            aria-label="Delete location"
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <Seo title="Locations" path="/admin/locations" />

      <AdminPageHeader
        icon="ri-map-pin-line"
        title="Locations"
        subtitle="The districts and towns covered by the directory."
        actions={
          <>
            {actionError ? (
              <span className="inline-flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-2 text-sm text-secondary-900">
                <i className="ri-error-warning-line text-secondary-600"></i> {actionError}
              </span>
            ) : notice ? (
              <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
                <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => (showForm ? resetForm() : setShowForm(true))}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className={showForm ? "ri-close-line" : "ri-add-line"}></i> {showForm ? "Cancel" : "New location"}
            </button>
          </>
        }
      />

      {showForm && (
        <form onSubmit={submit} className="mt-6 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 lg:flex-row lg:items-end">
          <div className="flex-1">
            <label htmlFor="locName" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">Name</label>
            <input
              id="locName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Nanjangud"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:border-primary-400 focus:outline-none"
            />
          </div>
          <div className="flex-1">
            <label htmlFor="locDesc" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">Description</label>
            <input
              id="locDesc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short summary"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:border-primary-400 focus:outline-none"
            />
          </div>
          <div className="lg:w-56">
            <label htmlFor="locDistrict" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">District</label>
            <select
              id="locDistrict"
              value={districtId}
              onChange={(e) => setDistrictId(e.target.value)}
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:border-primary-400 focus:outline-none cursor-pointer"
            >
              {districts.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={saving || !name.trim()}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap cursor-pointer"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
            {editingId ? "Save changes" : "Save"}
          </button>
        </form>
      )}

      <div className="mt-6">
        <AdminToolbar query={query} onQuery={setQuery} placeholder="Search locations…" />
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="h-72 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ) : error ? (
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center text-sm text-secondary-900">
            Couldn't load locations. Please refresh.
          </div>
        ) : (
          <DataTable columns={columns} rows={filtered} rowKey={(r) => r.id} minWidth="860px" />
        )}
      </div>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete this location?"
        message={pendingDelete ? `“${pendingDelete.name}” will be removed from the directory. Listings mapped to it may lose their locality. This cannot be undone.` : ""}
        confirmLabel="Delete location"
        busy={deleting}
        onConfirm={async () => {
          const target = pendingDelete;
          if (!target) return;
          setDeleting(true);
          await remove(target);
          setDeleting(false);
          setPendingDelete(null);
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}